import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, Calendar } from "lucide-react";
import { MonthlyComparison } from "@/hooks/use-dashboard-analytics";
import { useValuesVisibility } from "@/hooks/use-values-visibility";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface MonthlyComparisonCardProps {
  data: MonthlyComparison;
  isLoading?: boolean;
}

export function MonthlyComparisonCard({ data, isLoading }: MonthlyComparisonCardProps) {
  const { mask } = useValuesVisibility();
  const currentMonth = format(new Date(), "MMMM", { locale: ptBR });
  const capitalizedMonth = currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{capitalizedMonth}</CardTitle>
          <CardDescription>Resumo do mês</CardDescription>
        </CardHeader>
        <CardContent className="h-[120px] flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  const { 
    currentMonthExpenses, 
    previousMonthExpenses, 
    percentageChange, 
    daysRemaining,
    projectedTotal,
    dailyAverage,
    currentMonthIncome,
    previousMonthIncome,
    incomePercentageChange,
    incomeProjectedTotal,
    incomeDailyAverage,
    fixedExpensesTotal,
    variableExpensesProjected,
    fixedIncomeTotal,
    variableIncomeProjected,
  } = data;

  const isExpenseIncrease = percentageChange > 0;
  const isExpenseNeutral = Math.abs(percentageChange) < 5;

  const isIncomeIncrease = incomePercentageChange > 0;
  const isIncomeNeutral = Math.abs(incomePercentageChange) < 5;

  const netBalance = currentMonthIncome - currentMonthExpenses;

  const formatCurrency = (value: number) =>
    mask(new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value));

  const TrendIcon = ({ percentage, positiveIsGood }: { percentage: number; positiveIsGood: boolean }) => {
    const isNeutral = Math.abs(percentage) < 5;
    const isUp = percentage > 0;
    const colorClass = isNeutral
      ? "text-muted-foreground"
      : (isUp === positiveIsGood)
        ? "text-success"
        : "text-destructive";

    return (
      <div className={`flex items-center gap-1 text-sm ${colorClass}`}>
        {isNeutral ? (
          <Minus className="h-3.5 w-3.5" />
        ) : isUp ? (
          <TrendingUp className="h-3.5 w-3.5" />
        ) : (
          <TrendingDown className="h-3.5 w-3.5" />
        )}
        <span className="font-medium text-xs">
          {isNeutral ? "≈" : isUp ? "+" : ""}
          {percentage.toFixed(0)}%
        </span>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{capitalizedMonth}</CardTitle>
            <CardDescription>Resumo do mês</CardDescription>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{daysRemaining} dias restantes</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Income Section */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Receitas</p>
            <TrendIcon percentage={incomePercentageChange} positiveIsGood={true} />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xl font-bold text-success">{formatCurrency(currentMonthIncome)}</p>
            <span className="text-xs text-muted-foreground">
              vs {formatCurrency(previousMonthIncome)}
            </span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span
              title={`Fixo: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(fixedIncomeTotal)} + Variável: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(variableIncomeProjected)}`}
              className="cursor-help border-b border-dotted border-muted-foreground/40"
            >
              Projeção: {formatCurrency(incomeProjectedTotal)}
            </span>
            <span>{formatCurrency(incomeDailyAverage)}/dia</span>
          </div>
        </div>

        <div className="border-t border-border" />

        {/* Expense Section */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Despesas</p>
            <TrendIcon percentage={percentageChange} positiveIsGood={false} />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xl font-bold text-destructive">{formatCurrency(currentMonthExpenses)}</p>
            <span className="text-xs text-muted-foreground">
              vs {formatCurrency(previousMonthExpenses)}
            </span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span
              title={`Fixo: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(fixedExpensesTotal)} + Variável: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(variableExpensesProjected)}`}
              className="cursor-help border-b border-dotted border-muted-foreground/40"
            >
              Projeção: {formatCurrency(projectedTotal)}
            </span>
            <span>{formatCurrency(dailyAverage)}/dia</span>
          </div>
        </div>

        <div className="border-t border-border" />

        {/* Net Balance */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-sm font-medium">Saldo do mês</span>
          <span className={`text-lg font-bold ${netBalance >= 0 ? "text-success" : "text-destructive"}`}>
            {formatCurrency(netBalance)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
